import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiMessageCircle, FiHelpCircle, FiSend } from "react-icons/fi";

interface FAQItemProps {
    question: string;
    answer: string;
    isOpen: boolean;
    onClick: () => void;
    index: number;
    isRTL: boolean;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick, index, isRTL }) => {
    return (
        <motion.div
            dir={isRTL ? "rtl" : "ltr"}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.06 }}
            viewport={{ once: true }}
            className={`rounded-2xl transition-all duration-300 overflow-hidden mb-3.5 border ${
                isOpen
                    ? "bg-white border-[#0072CE]/50 shadow-md ring-2 ring-[#0072CE]/10"
                    : "bg-white/90 hover:bg-white border-slate-200/80 hover:border-blue-200 shadow-2xs"
            }`}
        >
            <button
                onClick={onClick}
                className="w-full text-start p-4 sm:p-5 flex items-center justify-between gap-3 sm:gap-4 transition-colors cursor-pointer"
                aria-expanded={isOpen}
            >
                <h3
                    className={`text-sm sm:text-base font-bold transition-colors leading-snug flex-1 ${
                        isOpen ? "text-[#0038A8]" : "text-slate-800"
                    }`}
                >
                    {question}
                </h3>
                <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                        isOpen
                            ? "bg-blue-50 text-[#0072CE] rotate-180"
                            : "bg-slate-100 text-slate-500 hover:bg-blue-50 hover:text-[#0072CE]"
                    }`}
                >
                    <FiChevronDown className="w-4 h-4 sm:w-5 sm:h-5 transition-transform" />
                </div>
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-1 text-slate-600 text-xs sm:text-sm leading-relaxed sm:leading-7 text-start border-t border-slate-100/90 bg-slate-50/40">
                            {answer}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const EnhancedFAQ: React.FC = () => {
    const { t, i18n } = useTranslation("FAQ");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [openIndex, setOpenIndex] = useState<number | null>(0);
    const [showContactModal, setShowContactModal] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: "",
    });
    const [submitted, setSubmitted] = useState(false);

    const faqs = [
        {
            question: t("questions.0.question"),
            answer: t("questions.0.answer"),
        },
        {
            question: t("questions.1.question"),
            answer: t("questions.1.answer"),
        },
        {
            question: t("questions.2.question"),
            answer: t("questions.2.answer"),
        },
        {
            question: t("questions.3.question"),
            answer: t("questions.3.answer"),
        },
        {
            question: t("questions.4.question"),
            answer: t("questions.4.answer"),
        },
    ];

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
        setTimeout(() => {
            setFormData({ name: "", email: "", message: "" });
            setSubmitted(false);
            setShowContactModal(false);
        }, 1500);
    };

    return (
        <section dir={isRTL ? "rtl" : "ltr"} className="py-8 sm:py-12 relative overflow-hidden mx-auto">
            {/* Background elements */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50/40 via-white/80 to-slate-50/50 -z-10 pointer-events-none" />
            <div className="absolute top-1/3 start-4 w-72 h-72 rounded-full bg-blue-100/30 blur-3xl -z-5 pointer-events-none" />
            <div className="absolute bottom-10 end-4 w-80 h-80 rounded-full bg-cyan-100/30 blur-3xl -z-5 pointer-events-none" />

            <div className="container mx-auto relative z-10 max-w-6xl px-2 sm:px-4">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                    {/* FAQ Content List */}
                    <div className="lg:col-span-8 bg-slate-50/60 backdrop-blur-md p-3 sm:p-5 md:p-6 rounded-3xl border border-slate-200/70 shadow-xs">
                        {faqs.map((item, index) => (
                            <FAQItem
                                key={index}
                                question={item.question}
                                answer={item.answer}
                                isOpen={openIndex === index}
                                onClick={() => handleToggle(index)}
                                index={index}
                                isRTL={isRTL}
                            />
                        ))}
                    </div>

                    {/* Support & Visual Banner Card */}
                    <motion.div
                        className="lg:col-span-4 w-full h-full"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                        viewport={{ once: true }}
                    >
                        <div className="relative w-full h-full min-h-[380px] lg:min-h-[460px] rounded-3xl overflow-hidden bg-white/90 border border-slate-200/80 shadow-sm p-6 flex flex-col justify-between items-center text-center">
                            {/* Ask Question Pill Button */}
                            <motion.button
                                className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-[#0038A8] to-[#0072CE] hover:from-[#002b82] hover:to-[#005bb5] text-white text-xs sm:text-sm font-bold rounded-full shadow-md shadow-blue-500/20 flex items-center justify-center gap-2 cursor-pointer transition-all active:scale-95 z-10"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setShowContactModal(true)}
                            >
                                <FiMessageCircle className="w-4 h-4" />
                                <span>{t("askButton", { defaultValue: "سوال خود را بپرسید" })}</span>
                            </motion.button>

                            {/* Center Logo with Breathing Halo Orbit */}
                            <div className="relative my-6 flex items-center justify-center w-full">
                                <motion.div
                                    className="absolute w-44 h-44 sm:w-52 sm:h-52 rounded-full bg-blue-500/10 blur-xl pointer-events-none"
                                    animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
                                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                                />

                                <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-full border border-dashed border-blue-200 flex items-center justify-center absolute">
                                    <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full border border-blue-100 flex items-center justify-center bg-blue-50/40" />
                                </div>

                                <motion.div
                                    className="relative z-10 w-32 h-32 sm:w-36 sm:h-36 flex items-center justify-center"
                                    animate={{ y: [0, -6, 0] }}
                                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <img
                                        src="/media/Logo1.svg"
                                        alt={t("logoAlt", { defaultValue: "Delband Logo" })}
                                        className="w-full h-full object-contain drop-shadow-md"
                                    />
                                </motion.div>
                            </div>

                            {/* Bottom Tagline */}
                            <div className="text-center z-10">
                                <p className="text-[#002B5C] text-xs sm:text-sm font-bold">
                                    {t("tagline", { defaultValue: "پاسخگوی تمامی پرسش‌های شما هستیم" })}
                                </p>
                                <p className="text-slate-500 text-[11px] mt-1">
                                    پشتیبانی ۲۴ ساعته و آنلاین
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Contact Form Modal */}
            <AnimatePresence>
                {showContactModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowContactModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-100 text-start"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-9 h-9 rounded-xl bg-blue-50 text-[#0072CE] flex items-center justify-center">
                                        <FiHelpCircle className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-base text-slate-900">
                                            {t("askButton", { defaultValue: "ارسال پرسش" })}
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            کارشناسان ما در سریع‌ترین زمان پاسخ خواهند داد
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowContactModal(false)}
                                    className="text-slate-400 hover:text-slate-600 p-1 text-sm rounded-lg hover:bg-slate-100 cursor-pointer"
                                >
                                    ✕
                                </button>
                            </div>

                            {submitted ? (
                                <div className="py-8 text-center text-emerald-600 font-bold text-sm">
                                    پیام شما با موفقیت ارسال شد!
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-3.5">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            نام و نام خانوادگی
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#0072CE] focus:ring-2 focus:ring-blue-100"
                                            placeholder="نام شما"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            ایمیل یا شماره تماس
                                        </label>
                                        <input
                                            type="text"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#0072CE] focus:ring-2 focus:ring-blue-100"
                                            placeholder="example@mail.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                                            متن پرسش
                                        </label>
                                        <textarea
                                            name="message"
                                            rows={3}
                                            required
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#0072CE] focus:ring-2 focus:ring-blue-100 resize-none"
                                            placeholder="پرسش خود را بنویسید..."
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white font-bold text-sm shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer mt-2"
                                    >
                                        <FiSend className="w-4 h-4" />
                                        <span>ارسال پیام</span>
                                    </button>
                                </form>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default EnhancedFAQ;

